import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { HeaderImg } from './LogoLogin';
import { Spacer } from '../../../../common/spacer/Spacer';
import { wasSessionExpired } from '../../../../hooks/auth/auth.hook';

const LoginContainer = ({
  login, errors, resetState, loading,
}) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [sessionHasExpired] = useState(() => wasSessionExpired());

  useEffect(() => {
    resetState();
  }, []);

  const submitForm = (e) => {
    e.preventDefault();
    if (loading) {
      return;
    }
    login({ email: email.trim(), password });
  };

  return (
    <PageBackground>
      <LoginCard elevation={3}>
        <LogoWrapper>
          <HeaderImg size="72px" />
        </LogoWrapper>
        <Typography variant="h5" component="h1" align="center" sx={{ fontWeight: 600 }}>
          {t('Sign in')}
        </Typography>
        <Typography variant="body2" align="center" color="text.secondary">
          {t('Sign in to access the CRM')}
        </Typography>
        <Spacer medium />
        {sessionHasExpired && !errors && (
          <>
            <Alert severity="warning">{t('Your session has expired, please log in again.')}</Alert>
            <Spacer small />
          </>
        )}
        {errors && (
          <>
            <Alert severity="error">{t(`${errors}`)}</Alert>
            <Spacer small />
          </>
        )}
        <Form
          method="post"
          id="loginForm"
          onSubmit={(e) => submitForm(e)}
        >
          <TextField
            label={t('Email')}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            autoFocus
            required
            fullWidth
            margin="dense"
            color="secondary"
            disabled={loading}
          />
          <TextField
            label={t('Password')}
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
            fullWidth
            margin="dense"
            color="secondary"
            disabled={loading}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={showPassword ? t('Hide password') : t('Show password')}
                    onClick={() => setShowPassword((show) => !show)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Spacer medium />
          <Button
            color="secondary"
            type="submit"
            variant="contained"
            size="large"
            fullWidth
            form="loginForm"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
          >
            {loading ? t('Signing in…') : t('Log in')}
          </Button>
        </Form>
      </LoginCard>
    </PageBackground>
  );
};

const PageBackground = styled.section`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f6f7f9;
  padding: 24px;
`;

const LoginCard = styled(Paper)`
  && {
    width: 100%;
    max-width: 400px;
    padding: 40px 32px 32px;
    display: flex;
    flex-direction: column;
    border-radius: 12px;
  }
`;

const LogoWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
`;

export {
  LoginContainer,
};
